import * as React from 'react';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { VISUAL, statusPill } from '../../../design-system/visual';
import { Plus, Pencil, Trash2, AlertTriangle } from 'lucide-react';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { hrApi } from '../api';
import { MonthCalendar, type DayCellData } from '../components/MonthCalendar';
import { ManualAttendanceDialog } from '../components/ManualAttendanceDialog';
import { EditAttendanceDialog } from '../components/EditAttendanceDialog';
import { AgentSetupCard } from '../../monitoring/components/AgentSetupCard';
import type { EmployeeWithUser, AttendanceSession, DailyAttendanceSummary } from '../types';

function pad(n: number) { return n < 10 ? `0${n}` : String(n); }

function monthRange(yyyymm: string): { from: string; to: string } {
  const [y, m] = yyyymm.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m)}-${pad(last)}` };
}

export function AttendancePage() {
  // Try to detect whether the current user is an employee with self clock-in.
  const myOpenQ = useApi<{ session: AttendanceSession | null }>('/api/hr/attendance/me/open');
  const isEmployee = myOpenQ.error == null;

  const empQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = empQ.data?.employees ?? [];
  // Employees without HR visibility (403 on the employee list) fall back to the
  // /attendance/me endpoints — their own calendar instead of a blank page.
  const selfMode = empQ.error != null;

  const today = new Date().toISOString().slice(0, 10);
  const [month, setMonth] = React.useState<string>(today.slice(0, 7));
  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<string | null>(null);
  const [selectedDate, setSelectedDate] = React.useState<string>(today);

  // Default to first employee, or current user's employee if employees list is empty.
  React.useEffect(() => {
    if (!selectedEmployeeId && employees.length > 0) setSelectedEmployeeId(employees[0].id);
  }, [employees, selectedEmployeeId]);

  const { from, to } = monthRange(month);
  const summaryPath = selfMode
    ? `/api/hr/attendance/me/summary?from=${from}&to=${to}`
    : selectedEmployeeId
      ? `/api/hr/attendance/summary?employee_id=${selectedEmployeeId}&from=${from}&to=${to}`
      : null;
  const summaryQ = useApi<{ summary: DailyAttendanceSummary[] }>(
    summaryPath,
    [selfMode, selectedEmployeeId, month],
  );

  const data: Record<string, DayCellData> = {};
  for (const row of summaryQ.data?.summary ?? []) {
    data[row.date] = { date: row.date, total_minutes: row.total_minutes, session_count: row.session_count, has_open_session: row.has_open_session };
  }

  // HR can add manual entries when an employee list is visible (view_all context).
  const canAddEntry = employees.length > 0 && selectedEmployeeId != null;
  const [addOpen, setAddOpen] = React.useState(false);
  const [dayReloadKey, setDayReloadKey] = React.useState(0);
  const selectedEmployee = employees.find((e) => e.id === selectedEmployeeId);

  return (
    <div>
      <PageHeader
        eyebrow="Team / Attendance"
        title="Attendance"
        description="Clock in or out and review the month at a glance."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {/* Monitored employees can't clock in from the browser — show the app
            install/setup card right where they'd try (renders nothing for
            everyone else). */}
        <AgentSetupCard />
        {isEmployee && <ClockWidget reload={() => { myOpenQ.reload(); summaryQ.reload(); }} session={myOpenQ.data?.session ?? null} />}

        {employees.length > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground/80">Showing</span>
            <Select value={selectedEmployeeId ?? ''} onValueChange={setSelectedEmployeeId}>
              <SelectTrigger className="h-9 rounded-full bg-card/80 border-border/60 px-3.5 text-[12.5px] font-semibold w-auto min-w-[200px]">
                <SelectValue placeholder="Select employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className={[VISUAL.bento, 'p-6 sm:p-8'].join(' ')}>
          <MonthCalendar
            month={month} onMonthChange={setMonth}
            data={data} selectedDate={selectedDate} onSelectDate={setSelectedDate}
          />
        </div>

        {(selfMode || selectedEmployeeId) && (
          <div className={[VISUAL.bento, 'p-6'].join(' ')}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground/80">Sessions on {selectedDate}</p>
              {canAddEntry && (
                <Button size="sm" variant="outline" className="rounded-full h-8 gap-1.5" onClick={() => setAddOpen(true)}>
                  <Plus className="h-3.5 w-3.5" /> Add entry
                </Button>
              )}
            </div>
            <DayDetail
              key={dayReloadKey}
              employeeId={selfMode ? null : selectedEmployeeId}
              date={selectedDate}
              // Corrections are an HR-on-behalf action: the same context that
              // can add a manual entry can fix or remove one.
              canManage={canAddEntry}
              employeeName={selectedEmployee?.name}
              onChanged={() => { summaryQ.reload(); setDayReloadKey((k) => k + 1); }}
            />
          </div>
        )}
      </div>

      {selectedEmployeeId && (
        <ManualAttendanceDialog
          open={addOpen}
          onOpenChange={setAddOpen}
          employeeId={selectedEmployeeId}
          employeeName={selectedEmployee?.name}
          defaultDate={selectedDate}
          onCreated={() => { setAddOpen(false); summaryQ.reload(); setDayReloadKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}

function ClockWidget({ session, reload }: { session: AttendanceSession | null; reload: () => void }) {
  const [busy, setBusy] = React.useState(false);

  async function clockIn() {
    setBusy(true);
    try {
      await hrApi.clockInSelf();
      toast.success('Clocked in');
      reload();
    } catch (err: any) {
      if (err?.code === 'use_agent') {
        toast.error('Clock in from the Worqly desktop app', {
          description: 'Your account is monitored, so browser clock-in is turned off. The install & setup card is on your dashboard home.',
        });
      } else {
        toast.error('Could not clock in', { description: err.message });
      }
    }
    finally { setBusy(false); }
  }

  async function clockOut() {
    setBusy(true);
    try {
      await hrApi.clockOutSelf();
      toast.success('Clocked out');
      reload();
    } catch (err: any) { toast.error('Could not clock out', { description: err.message }); }
    finally { setBusy(false); }
  }

  return (
    <div className={[VISUAL.bento, 'p-5 sm:p-6 flex items-center justify-between gap-4'].join(' ')}>
      <div className="flex items-center gap-4 min-w-0">
        <div className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 shrink-0">
          <span className={[
            'h-2 w-2 rounded-full',
            session ? 'bg-saffron animate-pulse' : 'bg-muted-foreground/50',
          ].join(' ')} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.14em] font-semibold text-muted-foreground/80">Your shift</p>
          <p className="text-[13.5px] text-foreground font-medium mt-0.5 truncate">
            {session
              ? `Clocked in at ${new Date(session.clock_in_at).toLocaleString()}`
              : 'Not clocked in. Ready when you are.'}
          </p>
        </div>
      </div>
      {session ? (
        <Button onClick={clockOut} disabled={busy} variant="outline" className="rounded-full h-10 px-5 font-semibold gap-1.5">
          {busy ? 'Saving…' : 'Clock out'}
        </Button>
      ) : (
        <Button onClick={clockIn} disabled={busy} className="rounded-full h-10 px-5 font-semibold gap-1.5">
          {busy ? 'Saving…' : 'Clock in'}
        </Button>
      )}
    </div>
  );
}

interface DayDetailProps {
  /** `null` = self mode — read the caller's own sessions via /attendance/me. */
  employeeId: string | null;
  date: string;
  /** HR-on-behalf context: show the correct/remove affordances. */
  canManage?: boolean;
  employeeName?: string;
  onChanged?: () => void;
}

function DayDetail({ employeeId, date, canManage = false, employeeName, onChanged }: DayDetailProps) {
  // Fetch raw sessions for the day, since the summary only carries minutes.
  const nextDay = (() => {
    const d = new Date(date + 'T00:00:00Z');
    d.setUTCDate(d.getUTCDate() + 1);
    return d.toISOString().slice(0, 10);
  })();
  const { data, loading, reload } = useApi<{ rows: AttendanceSession[] }>(
    employeeId
      ? `/api/hr/attendance?employee_id=${employeeId}&from=${date}&to=${nextDay}`
      : `/api/hr/attendance/me?from=${date}&to=${nextDay}`,
    [employeeId, date],
  );
  const confirm = useConfirm();
  const [editing, setEditing] = React.useState<AttendanceSession | null>(null);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  async function removeSession(s: AttendanceSession) {
    const ok = await confirm({
      title: 'Delete this attendance record?',
      description: 'The clocked hours disappear from summaries and from any payroll run computed after this. This can\'t be undone.',
      destructive: true,
      confirmLabel: 'Delete record',
    });
    if (!ok) return;
    setDeletingId(s.id);
    try {
      await hrApi.deleteAttendanceSession(s.id);
      toast.success('Attendance record deleted');
      reload();
      onChanged?.();
    } catch (err: any) {
      toast.error('Could not delete record', { description: err.message });
    } finally { setDeletingId(null); }
  }

  if (loading) return <LoadingState variant="inline" />;
  const rows = (data?.rows ?? []).slice().reverse();

  if (rows.length === 0) {
    return <div className="text-xs text-muted-foreground">No sessions on {date}.</div>;
  }
  return (
    <>
      <ul className="text-xs grid gap-2">
        {rows.map((s) => {
          const isOpen = !s.clock_out_at;
          return (
            <li
              key={s.id}
              className={[
                'flex flex-col gap-0.5 rounded-xl px-2.5 py-2',
                isOpen
                  ? 'bg-coral-100/60 ring-1 ring-coral-200 dark:bg-coral-500/10 dark:ring-coral-500/30'
                  : 'ring-1 ring-transparent',
              ].join(' ')}
            >
              <div className="flex items-center gap-3 font-mono">
                <span>{new Date(s.clock_in_at).toLocaleTimeString()}</span>
                <span className="text-muted-foreground">→</span>
                <span>
                  {s.clock_out_at
                    ? new Date(s.clock_out_at).toLocaleTimeString()
                    : (
                      <span className={statusPill('warning')}>
                        <AlertTriangle className="h-3 w-3" />
                        Needs closing
                      </span>
                    )}
                </span>
                {s.source === 'manual' && (
                  <span className="font-sans text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground/70">Manual</span>
                )}
                {canManage && (
                  <span className="ml-auto flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="rounded-full h-7 px-2.5 text-[11.5px] gap-1"
                      onClick={() => setEditing(s)}
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={deletingId === s.id}
                      className="rounded-full h-7 px-2.5 text-[11.5px] gap-1 text-muted-foreground hover:text-destructive"
                      onClick={() => removeSession(s)}
                    >
                      <Trash2 className="h-3 w-3" />
                      {deletingId === s.id ? 'Deleting…' : 'Delete'}
                    </Button>
                  </span>
                )}
              </div>
              {isOpen && (
                <p className="font-sans text-[11.5px] text-coral-700 dark:text-coral-200 pl-0.5">
                  Never clocked out — this blocks every later clock-in until it's closed.
                </p>
              )}
              {s.notes && <p className="text-[12px] text-muted-foreground pl-0.5">{s.notes}</p>}
            </li>
          );
        })}
      </ul>
      <EditAttendanceDialog
        open={editing !== null}
        onOpenChange={(v) => { if (!v) setEditing(null); }}
        session={editing}
        employeeName={employeeName}
        onSaved={() => { setEditing(null); reload(); onChanged?.(); }}
      />
    </>
  );
}
